import { API_ENDPOINTS } from '../config/api';
import { authFetch } from './authFetch';

export interface AuditHistoryEntry {
  id: number;
  reference_id: string;
  application_name: string;
  section: string;
  parameter_name: string;
  field_changed: string;
  old_value: string | null;
  new_value: string | null;
  changed_by: string;
  changed_at: string;
  user_email?: string;
  change_description?: string;
}

export interface CreateAuditEntryData {
  reference_id: string;
  application_name: string;
  section: string;
  parameter_name: string;
  field_changed: string;
  old_value?: string | null;
  new_value: string | null;
  changed_by: string;
  user_email?: string;
  change_description?: string;
}

/**
 * Fetch audit history for a specific application and parameter
 */
export const fetchAuditHistory = async (referenceId: string, parameterName?: string): Promise<AuditHistoryEntry[]> => {
  try {
    let url = API_ENDPOINTS.AUDIT_HISTORY(referenceId);
    if (parameterName) {
      url += `?parameter_name=${encodeURIComponent(parameterName)}`;
    }

    const response = await authFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Failed to fetch audit history');
    }
  } catch (error) {
    console.error('Error fetching audit history:', error);
    throw error;
  }
};

/**
 * Create a new audit history entry
 */
export const createAuditEntry = async (auditData: CreateAuditEntryData): Promise<void> => {
  try {
    const response = await authFetch(API_ENDPOINTS.CREATE_AUDIT_ENTRY, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(auditData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to create audit entry');
    }
  } catch (error) {
    console.error('Error creating audit entry:', error);
    throw error;
  }
};

/**
 * Get current user name (placeholder implementation)
 */
export const getCurrentUser = (): string => {
  // This could be enhanced to get actual user info from Azure AD or context
  return 'Current User';
};

/**
 * Format the change description for display
 */
export const formatChangeDescription = (entry: AuditHistoryEntry): string => {
  const { field_changed, old_value, new_value, parameter_name } = entry;
  
  switch (field_changed) {
    case 'status':
      return `Changed ${parameter_name} status from "${old_value || 'Not Set'}" to "${new_value || 'Not Set'}"`;
    case 'remarks':
      if (old_value && new_value) {
        return `Updated remarks for ${parameter_name}`;
      } else if (new_value) {
        return `Added remarks for ${parameter_name}`;
      } else {
        return `Removed remarks for ${parameter_name}`;
      }
    case 'links':
      return `Updated links for ${parameter_name}`;
    case 'completion_date':
      return `Changed completion date for ${parameter_name} from "${old_value || 'Not Set'}" to "${new_value || 'Not Set'}"`;
    default:
      return `Updated ${field_changed} for ${parameter_name}`;
  }
};

/**
 * Format date for display
 */
export const formatAuditDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return dateString;
  }
};